// ═══════════════════════════════════════════════════════════════
// HelpCenterConfig.js — Configuration, constants, adapter interfaces
// Drop-in help center module: FAQ, live chat, tickets, push notifications
// ═══════════════════════════════════════════════════════════════

// ── Default Configuration ──
// Host apps override these via the `config` prop on <HelpCenter />
const DEFAULT_CONFIG = {
  // Branding
  appName: 'My App',
  primaryColor: '#d97706',      // amber-600
  logoUrl: null,                 // optional logo URL

  // Feature flags
  faqEnabled: true,
  chatEnabled: true,
  ticketsEnabled: true,

  // FAQ data — array of categories, each with questions
  // Example:
  // [
  //   {
  //     category: 'Getting Started',
  //     icon: 'rocket',
  //     questions: [
  //       { id: 'faq-1', question: 'How do I sign up?', answer: 'Click the Sign Up button...' },
  //       { id: 'faq-2', question: 'How do I reset my password?', answer: 'Go to Settings...' },
  //     ]
  //   }
  // ]
  faqs: [],

  // Agent presence
  heartbeatIntervalMs: 30000,    // agent pings every 30s
  presenceTimeoutMs: 60000,      // no ping for 60s = offline

  // Chat
  maxMessageLength: 2000,
  enableFileAttachments: false,  // v2 feature
  enableTypingIndicators: true,
  enableReadReceipts: true,

  // Tickets
  ticketAutoCloseAfterDays: 30,  // auto-close resolved tickets after 30 days

  // Canned responses — agents can insert pre-written replies
  // Example: [{ id: 'cr-1', label: 'Greeting', text: 'Hi! How can I help you today?' }]
  cannedResponses: [],

  // Notification messages
  messages: {
    noAgentsAvailable: "No support agents are available right now. We'll get back to you ASAP!",
    chatConnecting: 'Connecting you with support...',
    chatEnded: 'This chat has ended. Thank you!',
    ticketCreated: "We've received your message and will respond as soon as possible.",
    ticketResolved: 'Your support ticket has been resolved.',
  },
};

// ── Adapter Interfaces ──
// Host apps provide concrete implementations of these interfaces.
// Default adapters for Firebase RTDB are included in adapters/.

/**
 * StorageAdapter interface — abstract read/write/subscribe for any backend
 *
 * All methods are async unless noted.
 *
 * Required methods:
 *   get(path)                    → returns data at path, or null
 *   set(path, data)              → writes data at path
 *   update(path, partialData)    → merges partialData into existing data at path
 *   push(path, data)             → appends data under path, returns generated key
 *   remove(path)                 → deletes data at path
 *   list(path)                   → returns array of items under path
 *   subscribe(path, callback)    → real-time listener; returns unsubscribe function
 *   serverTimestamp()            → returns a server-side timestamp token
 */
class StorageAdapter {
  async get(path) { throw new Error('StorageAdapter.get() not implemented'); }
  async set(path, data) { throw new Error('StorageAdapter.set() not implemented'); }
  async update(path, partialData) { throw new Error('StorageAdapter.update() not implemented'); }
  async push(path, data) { throw new Error('StorageAdapter.push() not implemented'); }
  async remove(path) { throw new Error('StorageAdapter.remove() not implemented'); }
  async list(path) { throw new Error('StorageAdapter.list() not implemented'); }
  subscribe(path, callback) { throw new Error('StorageAdapter.subscribe() not implemented'); }
  serverTimestamp() { throw new Error('StorageAdapter.serverTimestamp() not implemented'); }
}

/**
 * PushAdapter interface — abstract push notification sending
 *
 * Required methods:
 *   sendToUser(userId, notification)  → sends push to a specific user
 *     notification: { title, body, data }
 *   sendToRole(role, notification)    → sends push to all users with a role (e.g., 'support')
 */
class PushAdapter {
  async sendToUser(userId, notification) { throw new Error('PushAdapter.sendToUser() not implemented'); }
  async sendToRole(role, notification) { throw new Error('PushAdapter.sendToRole() not implemented'); }
}

// ── Path Helpers ──
// Centralized path construction so adapters and components use consistent keys.
// All paths are relative to the help-center data root.
const PATHS = {
  // FAQ analytics
  faqVotes: (faqId) => `help-center/faq-votes/${faqId}`,

  // Chat
  chatSessions: () => 'help-center/chats',
  chatSession: (chatId) => `help-center/chats/${chatId}`,
  chatMessages: (chatId) => `help-center/chats/${chatId}/messages`,
  chatMessage: (chatId, msgId) => `help-center/chats/${chatId}/messages/${msgId}`,
  chatTyping: (chatId) => `help-center/chats/${chatId}/typing`,

  // Tickets
  tickets: () => 'help-center/tickets',
  ticket: (ticketId) => `help-center/tickets/${ticketId}`,
  ticketMessages: (ticketId) => `help-center/tickets/${ticketId}/messages`,

  // Agent presence
  // Grouped by organization; super admins share the '_all' group (see recordScope.js)
  agentPresence: (bucket) => (bucket ? `help-center/presence/${bucket}` : 'help-center/presence'),
  agentStatus: (bucket, agentId) => `help-center/presence/${bucket}/${agentId}`,
};

// ── Status Constants ──
const CHAT_STATUS = {
  WAITING: 'waiting',       // user sent first message, no agent yet
  ACTIVE: 'active',         // agent connected, live chat
  ENDED: 'ended',           // chat closed by either party
};

const TICKET_STATUS = {
  OPEN: 'open',             // new, unread by support
  IN_PROGRESS: 'in-progress', // agent is working on it
  RESOLVED: 'resolved',     // marked resolved by agent
};

const AGENT_STATUS = {
  ONLINE: 'online',
  AWAY: 'away',
  OFFLINE: 'offline',
};

// ── Utility: merge config with defaults ──
function mergeConfig(userConfig = {}) {
  return {
    ...DEFAULT_CONFIG,
    ...userConfig,
    messages: {
      ...DEFAULT_CONFIG.messages,
      ...(userConfig.messages || {}),
    },
  };
}

// ── Utility: generate unique IDs ──
function generateId(prefix = 'hc') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`;
}

export {
  DEFAULT_CONFIG,
  StorageAdapter,
  PushAdapter,
  PATHS,
  CHAT_STATUS,
  TICKET_STATUS,
  AGENT_STATUS,
  mergeConfig,
  generateId,
};
